import { useCallback } from 'react';
import { parseUnits } from 'ethers';

import { SelectedBorrowAsset } from '@/features/Modals';
import { useCentrifugeContext } from '@/app/providers/Centrifuge';
import { Agreement } from '@/shared/api/borrow';

type GetRiskFactorParams = {
    selectedAssets: SelectedBorrowAsset[];
    borrowAmount: string;
};

type EstimateRiskFactorResponse = {
    risk_factor: string;
    market_value: string;
    stress_tested_value: string;
};

type UseFetchRiskFactorProps = {
    agreement: Agreement;
    assetSymbol: string;
    agreementAddress: string;
};

export const useFetchRiskFactor = ({
    agreement,
    assetSymbol,
    agreementAddress,
}: UseFetchRiskFactorProps) => {
    const { centrifuge } = useCentrifugeContext();

    const fetchRiskFactor = useCallback(
        async ({
            selectedAssets,
            borrowAmount,
        }: GetRiskFactorParams): Promise<EstimateRiskFactorResponse | undefined> => {
            try {
                if (!selectedAssets || !selectedAssets.length) {
                    return;
                }

                const collateral = selectedAssets.map((item) => {
                    const rawAmount = item.amount || '0';
                    const cleanedAmount = rawAmount.toString().replace(/,/g, '');
                    return {
                        name: item.name,
                        amount: parseUnits(cleanedAmount.toString(), item.decimals).toString(),
                        address: item.address,
                        symbol: item.symbol,
                        decimals: item.decimals,
                    };
                });

                if (collateral.length === 0) {
                    return;
                }

                const params = {
                    leverage: {
                        address: agreement.leverage.address,
                        name: agreement.leverage.name,
                        symbol: assetSymbol,
                        decimals: agreement.leverage.decimals,
                        amount: parseUnits(borrowAmount, agreement.leverage.decimals).toString(),
                    },
                    collateral,
                    agreement_address: agreementAddress,
                };

                const response = await centrifuge?.rpc('estimate_risk_factor', params);

                let parsedData = response?.data;

                if (typeof response?.data === 'string') {
                    try {
                        parsedData = JSON.parse(response.data);
                    } catch (parseError) {
                        throw new Error('Failed to parse response data');
                    }
                }

                if (parsedData?.error) {
                    console.error(parsedData.error);
                    return;
                }

                return parsedData?.result as EstimateRiskFactorResponse;
            } catch (error) {
                console.error((error as Error).message);
            }
        },
        [],
    );

    return {
        fetchRiskFactor,
    };
};
